import React, { useState, useContext, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import {
  parse,
  format,
  differenceInMonths,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import industryLogo from "../../images/default_industry.png";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const years = Array.from(
  { length: 50 },
  (_, i) => new Date().getFullYear() - i
);

export default function ModifyExperience({
  experience,
  getExpertExperience,
  setIsModify,
  clickedFrom,
}) {
  const { API_URL } = useContext(UrlContext);
  const [experienceID] = useState(experience.experienceID);
  const [companyName, setCompanyName] = useState(experience.companyName);
  const [companyLogo, setCompanyLogo] = useState(experience.companyLogo);
  const [error, setError] = useState("");

  // new logo drag & drop
  const dropArea = useRef(null);
  const progressBarRef = useRef(null);

  const preventDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const highlight = () => dropArea.current.classList.add("ring-2", "ring-blue-300");
  const unhighlight = () => dropArea.current.classList.remove("ring-2", "ring-blue-300");

  useEffect(() => {
    const drop = dropArea.current;
    if (!drop) return;
    const handleDrop = (e) => {
      preventDefaults(e);
      unhighlight();
      handleFiles(e);
    };

    ["dragenter", "dragover"].forEach((event) =>
      drop.addEventListener(event, highlight, false)
    );
    ["dragleave", "drop"].forEach((event) =>
      drop.addEventListener(event, unhighlight, false)
    );
    drop.addEventListener("drop", handleDrop, false);

    return () => {
      ["dragenter", "dragover", "dragleave", "drop"].forEach((event) =>
        drop.removeEventListener(event, preventDefaults, false)
      );
    };
  }, []);

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: "image/png" });
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => setCompanyLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const handleHtmlImgUrl = async (imageURL) => {
    try {
      if (imageURL.includes("images.expertpowerhouse.com")) {
        // Directly use your own CDN logo (avoid CORS fetch)
        setCompanyLogo(imageURL);
        return;
      }
      const res = await fetch(imageURL, { mode: "cors" });
      const blob = await res.blob();
      previewFile(blob);
    } catch {
      Swal.fire("Error", "Could not load image from pasted content.", "error");
    }
  };

  const handleFiles = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      [...e.target.files].forEach((file) => previewFile(file));
      return;
    }

    if (e.dataTransfer.files.length > 0) {
      [...e.dataTransfer.files].forEach((file) => previewFile(file));
      return;
    }

    // Handle LinkedIn or HTML-wrapped images
    const htmlData = e.dataTransfer.getData("text/html");
    if (htmlData) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlData, "text/html");
      const imgElement = doc.querySelector("img");
      if (imgElement) {
        const imageURL = imgElement.getAttribute("src");
        try {
          if (imageURL.includes("images.expertpowerhouse.com")) {
            setCompanyLogo(imageURL);
            return;
          }

          if (imageURL.startsWith("data:image")) {
            const blob = dataURItoBlob(imageURL);
            previewFile(new File([blob], "image.png", { type: "image/png" }));
          } else {
            const res = await fetch(imageURL);
            if (!res.ok) throw new Error("Fetch failed");
            const blob = await res.blob();
            previewFile(new File([blob], "image.png", { type: "image/png" }));
          }
        } catch (err) {
          Swal.fire({ title: "Error", text: "Could not load dropped image.", icon: "error", showConfirmButton: false, timer: 1500, });
        }
      } else {
        Swal.fire({ title: "Invalid Drop", text: "Please drop a valid image file or element.", icon: "warning", showConfirmButton: false, timer: 1500, });
      }
    } else {
      Swal.fire({ title: "Invalid Drop", text: "No valid image detected.", icon: "warning", showConfirmButton: false, timer: 1500, });
    }
  };

  const handlePaste = async (e) => {
    const items = [...e.clipboardData.items];
    const imgItem = items.find((i) => i.type.indexOf("image") === 0);
    if (imgItem) {
      const file = imgItem.getAsFile();
      previewFile(file);
      return;
    }

    const html = e.clipboardData.getData("text/html");
    if (html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const img = doc.querySelector("img");
      if (img) {
        const src = img.getAttribute("src");
        if (src.startsWith("data:image")) {
          const blob = dataURItoBlob(src);
          previewFile(blob);
        } else {
          await handleHtmlImgUrl(src);
        }
        return;
      }
    }

    const text = e.clipboardData.getData("text/plain");
    if (text && /^https?:\/\//i.test(text)) {
      await handleHtmlImgUrl(text.trim());
      return;
    }

    Swal.fire({ title: "No Image Found", text: "Copy an image first, then paste here.", icon: "info", showConfirmButton: false, timer: 1500, });
  };

  const [isMultiPosition, setIsMultiPosition] = useState(
    Array.isArray(experience.subExperienceBlocks)
  );

  const [subExperienceBlocks, setSubExperienceBlocks] = useState(
    experience.subExperienceBlocks?.map((block) => {
      const isCurrent =
        !block.leavingDate ||
        ["present", "heute"].includes(block.leavingDate?.toLowerCase());
      return {
        ...block,
        joiningMonth: block.joiningDate?.split(" ")[0],
        joiningYear: block.joiningDate?.split(" ")[1],
        leavingMonth: isCurrent ? "" : block.leavingDate?.split(" ")[0],
        leavingYear: isCurrent ? "" : block.leavingDate?.split(" ")[1],
        isCurrent,
      };
    }) || []
  );

  const [singleExperience, setSingleExperience] = useState({
    designation: experience.designation || "",
    joiningMonth: experience.joiningDate?.split(" ")[0] || "",
    joiningYear: experience.joiningDate?.split(" ")[1] || "",
    leavingMonth: ["present", "heute"].includes(
      experience.leavingDate?.toLowerCase()
    )
      ? ""
      : experience.leavingDate?.split(" ")[0],
    leavingYear: ["present", "heute"].includes(
      experience.leavingDate?.toLowerCase()
    )
      ? ""
      : experience.leavingDate?.split(" ")[1],
    isCurrent:
      !experience.leavingDate ||
      ["present", "heute"].includes(experience.leavingDate?.toLowerCase()),
  });

  const calculateDuration = (start, end) => {
    if (!start) return "";
    const today = new Date();
    if (!end || ["present", "heute"].includes(end.toLowerCase()))
      end = format(today, "MMM yyyy");
    const s = startOfMonth(parse(start, "MMM yyyy", new Date()));
    const e = endOfMonth(parse(end, "MMM yyyy", new Date()));
    const totalMonths = differenceInMonths(e, s) + 1;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    return `${years > 0 ? `${years} yr${years > 1 ? "s" : ""} ` : ""}${
      months > 0 ? `${months} mo${months > 1 ? "s" : ""}` : ""
    }`.trim();
  };

  const calculateOverallDuration = (blocks) => {
    const joinDates = blocks.map((b) =>
      parse(`${b.joiningMonth} ${b.joiningYear}`, "MMM yyyy", new Date())
    );
    const leaveDates = blocks.map((b) => {
      if (b.isCurrent) return new Date(); // now
      return parse(
        `${b.leavingMonth} ${b.leavingYear}`,
        "MMM yyyy",
        new Date()
      );
    });
    const earliest = new Date(Math.min(...joinDates));
    const latest = new Date(Math.max(...leaveDates));
    return calculateDuration(
      format(earliest, "MMM yyyy"),
      format(latest, "MMM yyyy")
    );
  };

  const handleSubChange = (idx, field, value) => {
    const updated = [...subExperienceBlocks];
    updated[idx][field] = value;
    setSubExperienceBlocks(updated);
  };

  const toggleCurrentStatus = (idx) => {
    const updated = [...subExperienceBlocks];
    const block = updated[idx];
    const isNowCurrent = !block.isCurrent;

    updated[idx] = {
      ...block,
      isCurrent: isNowCurrent,
      leavingMonth: isNowCurrent ? "" : block.prevLeavingMonth || "",
      leavingYear: isNowCurrent ? "" : block.prevLeavingYear || "",
      prevLeavingMonth: !isNowCurrent ? undefined : block.leavingMonth,
      prevLeavingYear: !isNowCurrent ? undefined : block.leavingYear,
    };

    setSubExperienceBlocks(updated);
  };

  const addSub = () => {
    setSubExperienceBlocks([
      ...subExperienceBlocks,
      {
        designation: "",
        joiningMonth: "",
        joiningYear: "",
        leavingMonth: "",
        leavingYear: "",
        isCurrent: true,
      },
    ]);
  };

  const deleteSub = (idx) => {
    setSubExperienceBlocks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleMultiToggle = () => {
    setError("");
    if (!isMultiPosition) {
      const firstBlock = {
        designation: singleExperience.designation,
        joiningMonth: singleExperience.joiningMonth,
        joiningYear: singleExperience.joiningYear,
        leavingMonth: singleExperience.leavingMonth,
        leavingYear: singleExperience.leavingYear,
        isCurrent: singleExperience.isCurrent,
      };
      setSubExperienceBlocks([
        firstBlock,
        {
          designation: "",
          joiningMonth: "",
          joiningYear: "",
          leavingMonth: "",
          leavingYear: "",
          isCurrent: true,
        },
      ]);
    } else {
      const first = subExperienceBlocks[0] || {};
      setSingleExperience({
        designation: first.designation || "",
        joiningMonth: first.joiningMonth || "",
        joiningYear: first.joiningYear || "",
        leavingMonth: first.leavingMonth || "",
        leavingYear: first.leavingYear || "",
        isCurrent: first.isCurrent ?? true,
      });
      setSubExperienceBlocks([]);
    }
    setIsMultiPosition(!isMultiPosition);
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem("AccessToken");
    let payload;
    if (isMultiPosition) {
      payload = subExperienceBlocks.map((block) => {
        const join = `${block.joiningMonth} ${block.joiningYear}`;
        const leave = block.isCurrent
          ? "Present"
          : `${block.leavingMonth} ${block.leavingYear}`;
        return {
          ...block,
          joiningDate: join,
          leavingDate: leave,
          duration: calculateDuration(join, leave),
        };
      });
    }

    const request = {
      experienceID,
      companyName,
      companyLogo,
      subExperienceBlocks: isMultiPosition ? payload : null,
      designation: isMultiPosition ? null : singleExperience.designation,
      joiningDate: isMultiPosition
        ? null
        : `${singleExperience.joiningMonth} ${singleExperience.joiningYear}`,
      leavingDate: isMultiPosition
        ? null
        : singleExperience.isCurrent
        ? "Present"
        : `${singleExperience.leavingMonth} ${singleExperience.leavingYear}`,
      duration: isMultiPosition
        ? calculateOverallDuration(payload)
        : calculateDuration(
            `${singleExperience.joiningMonth} ${singleExperience.joiningYear}`,
            singleExperience.isCurrent
              ? "Present"
              : `${singleExperience.leavingMonth} ${singleExperience.leavingYear}`
          ),
    };

    try {
      const res = await fetch(
        `${API_URL}${
          clickedFrom === "profile"
            ? "update-expert-experience"
            : "update-power-point-experience"
        }`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ expertExperience: request }),
        }
      );
      const json = await res.json();
      if (json.status === "success") {
        Swal.fire({
          title: "Success!",
          text: "Changes were successfull!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500
        });
        getExpertExperience();
      } else Swal.fire({title: "Error", text: json.text || "Something went wrong", icon: "error", confirmButtonColor: "#365561"});
    } catch (err) {
      Swal.fire({title: "Error", text: err.message, icon: "error", confirmButtonColor: "#365561"});
    } finally {
      setIsModify(false);
    }
  };

  const deleteExpertExperience = async () => {
    const token = localStorage.getItem("AccessToken");
    const requestBody = {
      experienceID: experienceID,
    };
    try {
      let modifiedUrl = "delete-expert-experience";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        getExpertExperience();
        Swal.fire({
          title: "Success!",
          text: responseData.text,
          icon: "success",
          showConfirmButton: false,
          timer: 1500
        });
      } else if (responseData.status === "error") {
        Swal.fire({ title: "Error!", text: responseData.text, icon: "error", confirmButtonColor: "#365561" });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to delete certificate!",
          icon: "error",
          confirmButtonColor: "#365561"
        });
      }
    } catch (error) {
      Swal.fire({ title: "Error!", text: error, icon: "error", confirmButtonColor: "#365561" });
    } finally {
      setIsModify(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Company Logo Section */}
        <div className="w-full md:w-1/4 flex flex-col justify-start items-center gap-4">
          {/* Drag & Drop Upload Area */}
          <div
            ref={dropArea}
            tabIndex={0}
            className="w-full h-30 border-2 border-dashed rounded-lg flex flex-col items-center justify-between text-gray-400 hover:bg-gray-100 p-2 text-center transition relative focus:outline-none"
            onDragOver={preventDefaults}
            onDrop={handleFiles}
            onPaste={handlePaste}
            onMouseEnter={() => dropArea.current?.focus()}
          >
            {/* Logo preview area */}
            <div className="absolute w-20 h-20 rounded-full border flex items-center justify-center bg-white">
              {companyLogo ? (
                <img
                  src={
                    companyLogo.startsWith("/pictures/")
                      ? `https://images.expertpowerhouse.com${companyLogo}`
                      : companyLogo
                  }
                  alt="Company Logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = industryLogo;
                  }}
                  className="w-20 h-20 rounded-full object-contain"
                />
              ) : (
                <i className="fa-solid fa-building text-gray-300 text-3xl"></i>
              )}
            </div>

            <label  className="flex flex-col items-center mt-12">
              <i className="fa-solid fa-upload text-2xl mb-1"></i>
              <span className="text-xs">Drag & drop or paste (Ctrl+V or ⌘+V) inside the box to upload, or</span>
              <label htmlFor="logoUpload" className="text-blue-700 text-sm cursor-pointer">Browse file...</label>
            </label>

            <input
              id="logoUpload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFiles}
            />
          </div>

          {/* Action Buttons */}
          {companyLogo && (
            <div className="flex justify-center gap-3 mt-2">
              <button
                onClick={() => setCompanyLogo("")}
                className="px-3 py-1 text-sm border rounded-lg text-red-500 hover:bg-red-50 transition"
              >
                Remove logo
              </button>
            </div>
          )}
        </div>

        <div className="w-full md:w-3/4">
          <input
            className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 mb-1"
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />

          <div className="flex items-center text-sm text-gray-600 mb-2">
            <input
              type="checkbox"
              checked={isMultiPosition}
              onChange={handleMultiToggle}
              className="mr-2 cursor-pointer"
            />
            <span className="cursor-default">This role had multiple positions</span>
          </div>

          {isMultiPosition ? (
            <>
              <ul className="list-none mt-2 relative">
                {subExperienceBlocks.map((block, idx) => (
                  <li key={idx} className="relative pl-4 mb-6">
                    <div className="absolute -left-[9px] top-2 w-2 h-2 bg-gray-500 rounded-full"></div>
                    {idx < subExperienceBlocks.length - 1 && (
                      <div className="absolute -left-[6px] top-5 h-full w-px bg-gray-300"></div>
                    )}
                    <input
                      className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1"
                      placeholder="Designation"
                      value={block.designation}
                      onChange={(e) =>
                        handleSubChange(idx, "designation", e.target.value)
                      }
                    />
                    <div className="flex gap-2 mb-2">
                      <select
                        className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                        value={block.joiningMonth}
                        onChange={(e) =>
                          handleSubChange(idx, "joiningMonth", e.target.value)
                        }
                      >
                        <option value="">Month</option>
                        {months.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                        value={block.joiningYear}
                        onChange={(e) =>
                          handleSubChange(idx, "joiningYear", e.target.value)
                        }
                      >
                        <option value="">Year</option>
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                      <span className="self-center">-</span>
                      {!block.isCurrent ? (
                        <>
                          <select
                            className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                            value={block.leavingMonth}
                            onChange={(e) =>
                              handleSubChange(
                                idx,
                                "leavingMonth",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Month</option>
                            {months.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <select
                            className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                            value={block.leavingYear}
                            onChange={(e) =>
                              handleSubChange(
                                idx,
                                "leavingYear",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Year</option>
                            {years.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <span className="self-center text-gray-400">
                          Present
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <input
                        type="checkbox"
                        checked={block.isCurrent}
                        onChange={() => toggleCurrentStatus(idx)}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="cursor-default">Current Position</span>
                    </div>
                    <input
                      className="w-full bg-white focus:outline-none text-gray-400 text-sm pb-0.1"
                      value={calculateDuration(
                        `${block.joiningMonth} ${block.joiningYear}`,
                        block.isCurrent
                          ? "Present"
                          : `${block.leavingMonth} ${block.leavingYear}`
                      )}
                      disabled
                    />
                    <button
                      onClick={() => deleteSub(idx)}
                      className="text-red-500 text-sm hover:underline"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={addSub}
                className="text-blue-600 text-sm hover:underline mt-2"
              >
                + Add Another Role
              </button>
            </>
          ) : (
            <>
              <input
                className="w-full border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 mb-1"
                placeholder="Designation"
                value={singleExperience.designation}
                onChange={(e) =>
                  setSingleExperience((prev) => ({
                    ...prev,
                    designation: e.target.value,
                  }))
                }
              />
              <div className="flex gap-2 mb-2">
                <select
                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                  value={singleExperience.joiningMonth}
                  onChange={(e) =>
                    setSingleExperience((prev) => ({
                      ...prev,
                      joiningMonth: e.target.value,
                    }))
                  }
                >
                  <option value="">Month</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                  value={singleExperience.joiningYear}
                  onChange={(e) =>
                    setSingleExperience((prev) => ({
                      ...prev,
                      joiningYear: e.target.value,
                    }))
                  }
                >
                  <option value="">Year</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                <span className="self-center">-</span>
                {!singleExperience.isCurrent ? (
                  <>
                    <select
                      className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                      value={singleExperience.leavingMonth}
                      onChange={(e) =>
                        setSingleExperience((prev) => ({
                          ...prev,
                          leavingMonth: e.target.value,
                        }))
                      }
                    >
                      <option value="">Month</option>
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <select
                      className="border-b border-gray-300 hover:border-[#365561] focus:border-[#365561] focus:outline-none text-sm pb-0.1 bg-transparent"
                      value={singleExperience.leavingYear}
                      onChange={(e) =>
                        setSingleExperience((prev) => ({
                          ...prev,
                          leavingYear: e.target.value,
                        }))
                      }
                    >
                      <option value="">Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span className="self-center text-gray-400">Present</span>
                )}
              </div>
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <input
                  type="checkbox"
                  checked={singleExperience.isCurrent}
                  onChange={() =>
                    setSingleExperience((prev) => {
                      const isNowCurrent = !prev.isCurrent;
                      return {
                        ...prev,
                        isCurrent: isNowCurrent,
                        leavingMonth: isNowCurrent
                          ? ""
                          : prev.prevLeavingMonth || "",
                        leavingYear: isNowCurrent
                          ? ""
                          : prev.prevLeavingYear || "",
                        prevLeavingMonth: !isNowCurrent
                          ? undefined
                          : prev.leavingMonth,
                        prevLeavingYear: !isNowCurrent
                          ? undefined
                          : prev.leavingYear,
                      };
                    })
                  }
                  className="mr-2 cursor-pointer"
                />
                <span className="cursor-default">Current Position</span>
              </div>
              <input
                className="w-full bg-white focus:outline-none text-gray-400 text-sm pb-0.1"
                value={calculateDuration(
                  `${singleExperience.joiningMonth} ${singleExperience.joiningYear}`,
                  singleExperience.isCurrent
                    ? "Present"
                    : `${singleExperience.leavingMonth} ${singleExperience.leavingYear}`
                )}
                disabled
              />
            </>
          )}

          {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={() => deleteExpertExperience()}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Delete
            </button>
            <button
              onClick={handleUpdate}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Save
            </button>
            {/* <button onClick={() => setIsModify(false)} className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500">Cancel</button> */}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
